import React, { Suspense, lazy, useRef, useEffect, useState } from 'react';
import {Box, Container, Typography, CircularProgress, useTheme, alpha} from '@mui/material';
import HeroSection from './homePage/sections/HeroSection';
import TrustSection from './homePage/sections/TrustSection';
import FloatingMobileCta from './homePage/sections/FloatingMobileCta';

// Eager: Hero + Trust only. Everything below the fold is code-split.
const GoogleReviewsSection = lazy(() => import('./homePage/sections/GoogleReviewsSection'));
const PartnersSection = lazy(() => import('./homePage/sections/PartnersSection'));
const ServicesSection = lazy(() => import('./homePage/sections/ServicesSection'));
const HowItWorksSection = lazy(() => import('./homePage/sections/HowItWorksSection'));
const ServiceZoneMap = lazy(() => import('../components/ServiceZoneMap'));
const FaqSection = lazy(() => import('./homePage/sections/FaqSection'));
const FinalCtaSection = lazy(() => import('./homePage/sections/FinalCtaSection'));

const SectionFallback = () => (
  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
    <CircularProgress size={24} />
  </Box>
);

/**
 * Map section that only loads Leaflet when near the viewport
 */
const MapSectionLazy = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        background: (t) => t.palette.background.paper,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            Our Service Area
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            px: { xs: 0, sm: 2, md: 4 },
            py: { xs: 0, sm: 2 },
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 1200,
              height: { xs: 400, sm: 480, md: 560 },
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.08)}`,
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
            }}
          >
            {isInView ? (
              <Suspense fallback={<Box sx={{ height: '100%', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}><CircularProgress size={32} /></Box>}>
                <ServiceZoneMap />
              </Suspense>
            ) : (
              <Box sx={{ height: '100%', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

/**
 * HomePage - Main landing page
 * Refactored into modular sections for better maintainability
 * Mobile-first responsive design with sticky bottom CTA
 */
const HomePage = () => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: theme.palette.background.default,
                position: 'relative',
                // No extra padding needed with floating CTA
            }}
        >
            {/* Hero Section */}
            <HeroSection/>

            {/* Trust Badges Section */}
            <TrustSection/>

            {/* Google Reviews Section — right after trust badges */}
            <Suspense fallback={<SectionFallback />}>
              <GoogleReviewsSection/>
            </Suspense>

            {/* Partner / fleet logos — add images in src/assets/partners/ */}
            <Suspense fallback={<SectionFallback />}>
              <PartnersSection/>
            </Suspense>

            {/* Popular Services Section */}
            <Suspense fallback={<SectionFallback />}>
              <ServicesSection/>
            </Suspense>

            {/* How It Works Section */}
            <Suspense fallback={<SectionFallback />}>
              <HowItWorksSection/>
            </Suspense>

            {/* Service Zone Section - Map loads only when near viewport */}
            <MapSectionLazy />

            {/* FAQ Section */}
            <Suspense fallback={<SectionFallback />}>
              <FaqSection/>
            </Suspense>

            {/* Final CTA Section */}
            <Suspense fallback={<SectionFallback />}>
              <FinalCtaSection/>
            </Suspense>

            {/* Floating CTA on mobile when scrolling: Call + Quote buttons */}
            <FloatingMobileCta/>

            {/* Animation Styles for Hero Illustration */}
            <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
        </Box>
    );
};

export default HomePage;
